<template>
	<v-card class="comment-card elevation-0">
		<div class="d-flex flex-fill comment-card__outer-box">

			<router-link
				:to="{
					name: isCurrentUser(this.post.user.user_id) ? 'profile' : 'user-by-id',
					params: isCurrentUser(this.post.user.user_id) ? [] : {id: this.post.user.user_id}
				}"
				class="d-flex align-start text-decoration-none"
				:class="{ disabled: this.post.user.user_hidden }"
			>
				<Avatar class="mr-6 mb-3" size="51" :avatar="comment.authorAvatar" />
			</router-link>

			<div class="comment-card__info-box" style="width: 100%">
				<router-link
					:to="{
					name: isCurrentUser(this.post.user.user_id) ? 'profile' : 'user-by-id',
					params: isCurrentUser(this.post.user.user_id) ? [] : {id: this.post.user.user_id}
				}"
					class="d-flex justify-space-between text-decoration-none"
					:class="{ disabled: this.post.user.user_hidden }"
				>
					<div class="d-flex flex-column mr-1">
						<v-card-title class="px-0 pt-0 comment-card__title mb-1">
							{{ comment.authorName }}
						</v-card-title>
						<v-card-subtitle class="px-0 comment-card__subtitle">
							{{ comment.user.second_profile_row }}
						</v-card-subtitle>
					</div>
					<div class="comment-card__date">
						{{ comment.date }}
					</div>
				</router-link>


				<v-card-actions v-if="isAuthenticated" class="px-0 py-0 comment-card__input-area">
					<CommentEditForm
						v-if="isEdit"
						:comment="comment"
						v-click-outside="hideEditInput"
						@success="
							() => {
								this.isEdit = false
							}
						"
					/>
					<v-card-text
						v-else-if="contentFormateChangeMentions.length"
						class="px-0 pb-2 comment-card__content"
					>
						<span v-for="(item, index) in contentFormateChangeMentions" :key="index">
							<router-link v-if="item.link" :to="item.link">
								{{ item.value }}
							</router-link>
							<span v-else>
								{{ item.value }}
							</span>
						</span>
					</v-card-text>
					<v-card-text v-else class="px-0 pb-2 comment-card__content">
						<span>{{ this.comment.content }}</span>
					</v-card-text>
				</v-card-actions>

				<v-card-actions v-if="isAuthenticated" class="d-flex justify-space-between px-0 py-0">
					<div class="d-flex align-center">
						<span 
							:class="isCurrentUserLike ? 'comment-card__like-btn-active' : 'comment-card__like-btn'" 
							@click="handleLikeComment(comment)">
							<Icon
                                type='like'
                                color='var(--accent-color)'
                                :size='16'
								:pr="8"
                            />
							<span>
								{{ upvoteCount }}
							</span>
						</span>
						<span class="comment-card__replies-count mr-5 ml-2">
							<Icon
                                type='comment'
                                color='var(--accent-color)'
                                :size='16'
								:pr="8"
                            />
							<span class="comment-card__replies-count-num">
								{{ repliesCount }}
							</span>
						</span>
					</div>

					<div class="d-flex align-center">
						<v-btn
							v-if="isOwnCurrentUserComment"
							class="comment-card__reply-btn text--black px-0"
							@click="handleDeleteComment(comment)"
							v-ripple="false"
							color="transparent"
						>
							Delete
						</v-btn>
						<v-btn
							v-if="!isReply"
							class="comment-card__reply-btn text--black px-0 ml-4"
							@click="replyHandler"
							v-ripple="false"
							color="transparent"
						>
							Reply
						</v-btn>
					</div>
				</v-card-actions>

				<ReplyCommentForm
					v-if="isReply"
					:comment="comment"
					:post="post"

					v-click-outside="hideInput"
					:isReply="isReply"
					@success="successReply"
				/>

				<Reply
					class="mb-5 mt-5"
					v-for="reply in replies"
					:key="reply.id"
					:comment="reply"
					:post="{
						thread_id: reply.threadId,
						user: reply.user
					}"
					:parent-comment="comment"
				/>
			</div>
		</div>
	</v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import { normalizeComment } from '@/utils/helpers'

import Avatar from '@/components/Avatar'
import ReplyCommentForm from '@/components/Forms/ReplyCommentForm'
import CommentEditForm from '@/components/Forms/CommentEditForm'
import Reply from '@/components/Reply'
export default {
	components: {
		Avatar,
		ReplyCommentForm,
		CommentEditForm,
		Reply,
	},
	props: {
		comment: {
			type: Object,
			default: () => {},
		},
		post: {
			type: Object,
			default: () => {}
		}
	},
	data: () => ({
		isReply: false,
		inputFocus: false,
		isEdit: false,
		isLike: false
	}),
	computed: {
		...mapGetters('auth', ['profile', 'isAuthenticated']),
		userName() {
			return this.profile.name
		},
		/**
		 * @returns Array
		 */
		replies() {
			return this.comment.replies.map((item) => normalizeComment(item)).reverse()
		},
		isCurrentUserLike() {
			return !!this.comment.upvote;
		},
		upvoteCount() {
			return this.comment.upvoteCount || 0;
		},
		repliesCount() {
			return this.comment.replies.length;
		},
		isOwnCurrentUserComment() {
			return this.comment.userId === this.profile.user_id
		},
		dateCreated() {
			const date = new Date(this.comment.date)
			const options = { year: 'numeric', month: 'long', day: 'numeric' }
			return date.toLocaleDateString('en-US', options)
		},
		contentFormateChangeMentions() {
			const content = this.comment.content
			const pattern = /@\[\d+\|[^]+?\]/g;
			const matches = content.match(pattern);
			let result = content
			let contentArray = []

			if (matches) {
				for (let i = 0; i < matches.length; i++) {
					const user_name = matches[i].match(/\|(.+?)\]/)[1];
					const user_id = matches[i].match(/\[(.*?)\|/)[1];
					
					const replacement = `@${user_name}`
					result = result.replace(matches[i], replacement);
					const startIndex = result.indexOf(`@${user_name}`)
					const endIndex = startIndex + user_name.length + 1
					if (startIndex > 0) {
						contentArray.push({value: result.substring(0, startIndex)})
					}
					contentArray.push({value: result.substring(startIndex, endIndex), link: {name: 'user-by-id', params: {id: user_id}}})
					if (i + 1 === matches.length && result.substring(endIndex)) {
						contentArray.push({value: result.substring(endIndex)})
					} else {
						result = result.substring(endIndex)
					}
				}
			}
			return contentArray
		}
	},
	methods: {
		...mapActions('post', ['deleteComment', 'likeComment']),

		successReply() {
			this.isReply = false
		},

		isCurrentUser(userId) {
			if (!this.profile) {
				return false
			}

			const {user_id: currentUserId} = this.profile
			return userId === currentUserId;
		},

		handleLikeComment(comment) {
			this.likeComment(comment)
		},
		handleDeleteComment(comment) {
			this.deleteComment(comment)
		},
		replyHandler() {
			this.inputFocus = true
			this.isReply = !this.isReply
		},

		hideInput() {
			this.isReply = false
		},
		hideEditInput() {
			this.isEdit = false
		}
	},
}
</script>

<style lang="scss">

.v-card {
	&__actions {
		align-items: flex-start;
	}
}

.comment-card {
	font-family: Montserrat, sans-serif;
	background-color: rgb(246, 246, 246) !important;
	font-style: normal;
	font-weight: normal;

	&__title {
		font-style: normal;
		font-weight: 700;
		font-size: 16px;
		line-height: 20px;
		color: var(--username-category-title);
	}

	&__subtitle {
		color: var(--company-position-large);
		font-size: 15px;
		line-height: 20px;
	}

	&__date {
		font-size: 13px;
		line-height: 19.37px;
		color: #697583;
	}

	&__reply-btn {
		display: flex;
		justify-content: flex-start;
		align-items: flex-start;
		margin-top: 2px;
		background-color: transparent !important;
		box-shadow: none !important;
		font: 14px/1.2 'Montserrat' !important;
		text-transform: none;
		letter-spacing: 0.35px;
		color: var(--button-secondary-background-color) !important;
		mix-blend-mode: normal;
		min-width: auto !important;
		line-height: 17.07px;
		font-weight: 700 !important;
		text-decoration: underline;

		&:hover {
			background: none;
			text-decoration: none;
		}

		&:before {
			content: none;
		}
	}

	&__reply {
		border-left: 1px solid rgba(0, 0, 0, 0.2);
		padding-left: 10px;
	}
	&__reply-info {
		display: flex;
		flex-direction: column;
		margin-bottom: 10px;
	}

	&__reply-author {
		font: 14px/1.2 'Montserrat' !important;
		color: #525252 !important;
	}
	&__reply-date {
		font: 12px/1.2 'Montserrat' !important;
		padding-top: 5px;
		color: rgba(0, 0, 0, 0.4) !important;
	}
	&__send-btn {
		width: 110px;
		height: 30px !important;
		border-radius: 24.5px;
		background-color: var(--accent-color) !important;
		color: #ffffff !important;
		font-family: Montserrat, sans-serif;
		text-transform: none;
		font-style: normal;
		font-weight: 500;
		font-size: 14px;
		line-height: 16px;
		text-align: center;
		&:hover {
			color: #fff !important;
			background-color: var(--accent-color) !important;
			opacity: 1;
		}
	}

	&__input-area {
		a {
			text-decoration: none;
		}
	}

	&__reply-field {
		border: 1px solid rgba(0, 0, 0, 0.15) !important;
		padding: 10px;
		margin-bottom: 10px;
		&:focus {
			outline: none !important;
		}
	}

	&__content {
		font-size: 16px;
		line-height: 25px;
		color: #000000;
		& a {
			font-weight: bold;
			color: #42a0d8;
			cursor: pointer;
		}
	}

	&__like-btn, &__like-btn-active {
		font-family: Montserrat, sans-serif;
		font-style: normal;
		font-weight: 500;
		font-size: 13px;
		height: 16px;
		line-height: 16px;
		cursor: pointer;
		display: flex;
		align-items: center;
	}
	&__like-btn {
		i, span {
			color: var(--accent-color) !important;
		}

		&-active {
			i, span {
				color: #a0a0a0 !important;
			}
		}
	}

	&__replies-count {
		display: flex;
		align-items: center;
		&-icon {
			color: var(--accent-color) !important;
			position: relative;
			top: 2px;
		}
		&-num {
			font-style: normal;
			font-weight: 500;
			font-size: 13px;
			line-height: 16px;
			color: var(--accent-color);
		}
	}

	@media screen and (max-width: 400px) {
		&__outer-box {
			flex-direction: column;
		}
		&__info-box {
			width: 100%;
		}
	}
}
</style>
